'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';

interface ActivityRecord {
  id: string;
  type: string;
  content: string;
  ip: string;
  location: string;
  createdAt: string;
  downloadCount: number;
}

interface AdminActivityTableProps {
  records: ActivityRecord[];
}

const typeConfig: Record<string, { label: string; badgeClass: string }> = {
  url: { label: 'URL', badgeClass: 'badge-blue' },
  upi: { label: 'UPI', badgeClass: 'badge-orange' },
  whatsapp: { label: 'WhatsApp', badgeClass: 'badge-green' },
  wifi: { label: 'WiFi', badgeClass: 'badge-amber' },
  email: { label: 'Email', badgeClass: 'badge-violet' },
  phone: { label: 'Phone', badgeClass: 'badge-cyan' },
  maps: { label: 'Maps', badgeClass: 'badge-rose' },
  text: { label: 'Text', badgeClass: 'badge-slate' },
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()} · ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

export default function AdminActivityTable({ records: initialRecords }: AdminActivityTableProps) {
  const [records, setRecords] = useState<ActivityRecord[]>(initialRecords);

  const handleDelete = (id: string) => {
    setRecords(prev => prev.filter(r => r.id !== id));
    toast.success('Record deleted.');
  };

  return (
    <div className="bg-card rounded-3xl border border-border shadow-card overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-foreground" style={{ fontSize: '15px' }}>Recent Activity</h3>
          <p className="text-muted-foreground" style={{ fontSize: '12px' }}>Latest QR codes generated on the platform</p>
        </div>
        <span className="badge badge-blue">{records.length} recent</span>
      </div>

      {/* Mobile card list — visible below md */}
      <div className="md:hidden divide-y divide-border">
        {records.map(record => {
          const cfg = typeConfig[record.type] || { label: record.type, badgeClass: 'badge-slate' };
          return (
            <div key={record.id} className="px-4 py-4 flex items-start gap-3">
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`badge ${cfg.badgeClass}`}>{cfg.label}</span>
                  <span className="text-muted-foreground" style={{ fontSize: '11px' }}>{formatTime(record.createdAt)}</span>
                </div>
                <p className="font-medium text-foreground truncate" style={{ fontSize: '13px' }} title={record.content}>
                  {record.content}
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-muted-foreground" style={{ fontSize: '11px' }}>{record.ip}</span>
                  <span className="text-muted-foreground" style={{ fontSize: '11px' }}>{record.location}</span>
                  <div className="flex items-center gap-1">
                    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="var(--muted-foreground)" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M6 8V2M6 8L4 6M6 8L8 6" />
                      <path d="M2 10h8" />
                    </svg>
                    <span className="text-muted-foreground" style={{ fontSize: '11px', fontVariantNumeric: 'tabular-nums' }}>{record.downloadCount} downloads</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleDelete(record.id)}
                className="flex-shrink-0 p-2.5 rounded-xl text-rose-400 hover:text-rose-600 hover:bg-rose-50 transition-all"
                style={{ minWidth: '40px', minHeight: '40px' }}
                title="Delete this activity record"
                aria-label="Delete record"
              >
                <svg width="14" height="14" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                  <path d="M2 3.5h9M4.5 3.5V2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5v1M10.5 3.5l-.5 7.5a1 1 0 0 1-1 .9H4a1 1 0 0 1-1-.9L2.5 3.5" />
                </svg>
              </button>
            </div>
          );
        })}
        {records.length === 0 && (
          <div className="py-12 flex flex-col items-center text-center px-4">
            <p className="font-semibold text-foreground" style={{ fontSize: '15px' }}>No activity records</p>
            <p className="text-muted-foreground mt-1" style={{ fontSize: '13px' }}>All records have been deleted.</p>
          </div>
        )}
      </div>

      {/* Desktop table — visible from md */}
      <div className="hidden md:block overflow-x-auto scrollbar-thin">
        <table className="w-full min-w-[680px]">
          <thead>
            <tr className="border-b border-border bg-secondary/40">
              {['Content', 'Type', 'IP Address', 'Location', 'Time', 'Downloads', ''].map((h, i) => (
                <th
                  key={`ah-${i + 1}`}
                  className="px-5 py-3 text-left"
                  style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted-foreground)', letterSpacing: '0.05em', textTransform: 'uppercase' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {records.map((record, idx) => {
              const cfg = typeConfig[record.type] || { label: record.type, badgeClass: 'badge-slate' };
              return (
                <tr
                  key={record.id}
                  className={`border-b border-border last:border-0 group transition-colors hover:bg-primary/4 ${idx % 2 === 0 ? 'bg-card' : 'bg-secondary/20'}`}
                >
                  <td className="px-5 py-3.5 max-w-xs">
                    <p className="font-medium text-foreground truncate" style={{ fontSize: '13px', maxWidth: '200px' }} title={record.content}>
                      {record.content}
                    </p>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`badge ${cfg.badgeClass}`}>{cfg.label}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="font-mono text-muted-foreground" style={{ fontSize: '12px' }}>{record.ip}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-foreground" style={{ fontSize: '13px' }}>{record.location}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-muted-foreground" style={{ fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>{formatTime(record.createdAt)}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="var(--muted-foreground)" strokeWidth="1.5" strokeLinecap="round">
                        <path d="M6 8V2M6 8L4 6M6 8L8 6" />
                        <path d="M2 10h8" />
                      </svg>
                      <span className="font-semibold text-foreground" style={{ fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>{record.downloadCount}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <button
                      onClick={() => handleDelete(record.id)}
                      className="p-1.5 rounded-xl text-rose-400 hover:text-rose-600 hover:bg-rose-50 transition-all opacity-0 group-hover:opacity-100"
                      title="Delete this activity record"
                      aria-label="Delete record"
                    >
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                        <path d="M2 3.5h9M4.5 3.5V2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5v1M10.5 3.5l-.5 7.5a1 1 0 0 1-1 .9H4a1 1 0 0 1-1-.9L2.5 3.5" />
                      </svg>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {records.length === 0 && (
        <div className="hidden md:flex py-16 flex-col items-center text-center">
          <div className="w-14 h-14 rounded-2xl gradient-primary-soft border border-border flex items-center justify-center mb-3">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.6" strokeLinecap="round" opacity="0.5">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16v.5" />
            </svg>
          </div>
          <p className="font-semibold text-foreground" style={{ fontSize: '15px' }}>No activity records</p>
          <p className="text-muted-foreground mt-1" style={{ fontSize: '13px' }}>All records have been deleted.</p>
        </div>
      )}
    </div>
  );
}